import { Constants } from '@repobit/dex-constants';
import { AdobeDataLayerService, AlloyResponsePropositions, CdpEvent, DecisionsDisplayedEvent, PageLoadStartedEvent } from '@repobit/dex-data-layer';
import {
  AlloyAppendIdentityToUrlResponse,
  AlloyApplyPropositionsResponse,
  AlloyFunction,
  AlloySendEventResponse,
  AppendIdentityToUrlCall,
  ApplyPropositionsCall,
  CdpData,
  CdpDataJson,
  ConfigMbox,
  GetIdentityCall,
  GetIdentityResponse,
  MboxParameters,
  MboxProfileParameters,
  SendEventCall,
  URLParameters
} from './typeDefinitions';

declare global {
  interface Window {
    alloyProxy: AlloyFunction;
    BD: {
      state: {
        cdpDataPromise: Promise<CdpData>
      }
    }
  }
}

if (!window.Promise.withResolvers) {
  window.Promise.withResolvers = function<T>() {
    let resolve, reject;
    const promise = new Promise<T>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { resolve, reject, promise } as unknown as PromiseWithResolvers<T>;
  }
}

type JsonObject = Record<string, unknown>
type JsonArray = unknown[]

const parseIfJsonObjectOrArray = <T = JsonObject | JsonArray> (
  value: unknown
): T | string => {
  // already parsed
  if (typeof value === 'object' && value !== null) {
    return value as T
  }

  if (typeof value !== 'string') {
    return value as string
  }

  const trimmed = value.trim()

  // HTML bailout (A/B test content)
  if (trimmed.startsWith('<')) {
    return value
  }

  // only allow object or array shapes
  const looksLikeJson =
    (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
    (trimmed.startsWith('[') && trimmed.endsWith(']'))

  if (!looksLikeJson) {
    return value
  }

  try {
    const parsed = JSON.parse(trimmed)

    // reject primitives
    if (typeof parsed === 'object' && parsed !== null) {
      return parsed as T
    }

    return value
  } catch {
    return value
  }
}

type ParsedResult<T> =
  | { type: 'json'; value: T }
  | { type: 'string'; value: string }

export function parseIfJsonStrict<T = JsonObject | JsonArray>(
  value: unknown
): ParsedResult<T> {
  const parsed = parseIfJsonObjectOrArray<T>(value)

  return typeof parsed === 'string'
    ? { type: 'string', value: parsed }
    : { type: 'json', value: parsed }
}

export default class Target {
  private urlParameters       : URLParameters;
  private profileUrlParameters: { [key: string]: string | undefined };
  private cachedMboxes        : Map<string, Promise<AlloyResponsePropositions | undefined>>;
  private _configMbox         : Promise<ConfigMbox | undefined>;
  private controller          : AbortController = new AbortController();
  private _visitorInfo        : Promise<GetIdentityResponse>;
  private cdpData             : Promise<CdpData>;
  private pageLoadStarted     : PageLoadStartedEvent | undefined;

  constructor(config?: Partial<{ pageLoadStartedEvent: PageLoadStartedEvent }>) {
    this.urlParameters = this.getUrlParameters();
    this.profileUrlParameters = this.transfromIntoProfileParameters(this.urlParameters);
    this.cachedMboxes = new Map();
    this.pageLoadStarted = config?.pageLoadStartedEvent;

    if (!window.alloyProxy) {
      function __alloy(...args: SendEventCall): Promise<AlloySendEventResponse>;
      function __alloy(...args: AppendIdentityToUrlCall): Promise<AlloyAppendIdentityToUrlResponse>;
      function __alloy(...args: ApplyPropositionsCall): Promise<AlloyApplyPropositionsResponse>;
      function __alloy(...args: GetIdentityCall): Promise<GetIdentityResponse>;
      function __alloy(...args: SendEventCall | AppendIdentityToUrlCall | ApplyPropositionsCall | GetIdentityCall):
      Promise<AlloySendEventResponse | AlloyAppendIdentityToUrlResponse | AlloyApplyPropositionsResponse | GetIdentityResponse> {
        return new Promise((resolve, reject) => {
          window.alloyProxy.q.push([resolve, reject, args]);
        });
      }

      window.alloyProxy = __alloy as AlloyFunction;
      window.alloyProxy.q = [];
    }

    // check for dotest=1 in URL. If it exists abort all target calls
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('dotest') === '1') {
      this.abort();
    }

    this._visitorInfo = new Promise((resolve) => {
      if (this.controller.signal?.aborted) {
        resolve({});
      }

      window.alloyProxy('getIdentity').then(result => resolve(result))
        .catch(() => resolve({}));
      this.controller.signal?.addEventListener('abort', () => resolve({}));
    });
    this.cdpData = config?.pageLoadStartedEvent && urlParams.get('dotest') !== '1'
      ? this.sendCdpData(config?.pageLoadStartedEvent)
      : Promise.resolve({} as CdpData);
    this._configMbox = this.getOffers({ mboxNames: 'config-mbox' });
  }

  /** get an object containing all the url query parameters */
  private getUrlParameters(): URLParameters {
    const urlParams = new URLSearchParams(window.location.search);
    const parameters: URLParameters = {};

    urlParams.forEach((value: string, key: string) => {
      parameters[key] = value;
    });

    return parameters;
  }

  public get configMbox() {
    return this._configMbox;
  }

  public get visitorInfo() {
    return this._visitorInfo;
  }

  /** add adobe_mc parameter at the end of the url */
  public appendVisitorIDsTo(url: string): Promise<string> {
    if (this.controller.signal?.aborted || url.includes('adobe_mc')) {
      return Promise.resolve(url);
    }

    return new Promise((resolve) => {
      window.alloyProxy('appendIdentityToUrl', { url }).then(result => resolve(result.url))
        .catch(() => resolve(url));
      this.controller.signal?.addEventListener('abort', () => resolve(url));
    });
  }

  /** abort all the Target calls for casese where Target does not load */
  public abort(): void {
    this.controller.abort();
  }

  public async sendCdpData(pageLoadStartedEvent: PageLoadStartedEvent): Promise<CdpData> {
    const cdpDataPromise = Promise.withResolvers<CdpData>();
    let cdpData: CdpData = {};
    if (window.BD.state.cdpDataPromise) {
      return await window.BD.state.cdpDataPromise;
    }
    window.BD.state.cdpDataPromise = cdpDataPromise.promise;

    try {
      const ecid = (await this.visitorInfo)?.identity?.ECID;
      const cdpDataCall = await fetch(
        `${Constants.PUBLIC_URL_ORIGIN}/cdp/`,
        {
          method: 'POST',
          body  : JSON.stringify({
            mcvisid: ecid || '',
            ...pageLoadStartedEvent.page
          })
        }
      );

      /** @type {{auds: string[], mdl: {key: string, value: string}[], ub: any[] vid: string}} */
      const receivedCdpData: CdpDataJson = await cdpDataCall.json();
      cdpData = {
        auds: receivedCdpData?.auds[0] || ''
      };

      if (receivedCdpData.mdl) {
        cdpData = receivedCdpData?.mdl.reduce((acc, mdlValue) => {
          acc[mdlValue.key] = mdlValue.value;
          return acc;
        }, cdpData);
      }

      if (receivedCdpData.ub) {
        cdpData = receivedCdpData?.ub.reduce((acc, ubValue) => {
          acc[ubValue.key] = ubValue.value;
          return acc;
        }, cdpData);
      }

      AdobeDataLayerService.push(new CdpEvent(cdpData));
    } catch (e) {
      console.warn(e);
    }

    cdpDataPromise.resolve(cdpData);
    return cdpData;
  }

  private transfromIntoProfileParameters(object: { [key: string]: string | undefined }) {
    const profileObject: { [key: string]: string | undefined } = Object.keys(object).reduce((acc, key) => {
      acc[`profile.${key}`] = object[key];
      return acc;
    }, {} as { [key: string]: string | undefined });

    return profileObject;
  }

  public async getOffers(param: {
    mboxNames         : 'config-mbox',
    parameters?       : MboxParameters,
    profileParameters?: MboxProfileParameters
  }): Promise<ConfigMbox | undefined>;
  public async getOffers(param: {
    mboxNames         : string,
    parameters?       : MboxParameters,
    profileParameters?: MboxProfileParameters
  }): Promise<object | undefined>;
  public async getOffers(param: {
    mboxNames         : string[],
    parameters?       : MboxParameters,
    profileParameters?: MboxProfileParameters
  }): Promise<Record<string, object | undefined>>;
  public async getOffers(param: {
    mboxNames         : string[] | string,
    parameters?       : MboxParameters,
    profileParameters?: MboxProfileParameters
  }) {
    let { mboxNames } = param;
    const isInitialyArray = Array.isArray(mboxNames);
    const { parameters, profileParameters } = param;
    if (!Array.isArray(mboxNames)) {
      mboxNames = [mboxNames];
    }

    const cdpData = await this.cdpData;
    if (this.controller.signal?.aborted) {
      return mboxNames.length > 1 ? {} : undefined;
    }

    const notRequestedMboxes = mboxNames.filter(
      mbox => !this.cachedMboxes.has(`${mbox}_${JSON.stringify(parameters)}`)
    );
    if (notRequestedMboxes.length) {
      const notRequestedOffersCall = window.alloyProxy('sendEvent', {
        type          : 'decisioning.propositionFetch',
        decisionScopes: notRequestedMboxes,
        data          : {
          '__adobe': {
            'target': Object.assign(
              {},
              this.urlParameters,
              this.profileUrlParameters,
              parameters ? parameters : {},
              profileParameters ? profileParameters : {},
              cdpData,
              this.transfromIntoProfileParameters(cdpData)
            )
          }
        }
      });

      notRequestedOffersCall.then(result => {
        const htmlPropositions = result.propositions.filter(
          proposition => proposition?.items?.some(item => parseIfJsonStrict(item?.data?.content).type === 'string')
        );

        const jsonPropositions = result.propositions.filter(
          proposition => proposition?.items?.some(item => parseIfJsonStrict(item?.data?.content).type === 'json')
        ).map(proposition => {
          const { ...propositionCopy } = proposition;
          delete propositionCopy.items;
          delete propositionCopy.renderAttempted;
          return propositionCopy;
        });

        if (htmlPropositions.length) {
          window.alloyProxy('applyPropositions', {
            'propositions': htmlPropositions,
            'viewName'    : this.pageLoadStarted?.page?.info?.name || window.location.pathname
          });
        }

        if (jsonPropositions.length) {
          AdobeDataLayerService.push(new DecisionsDisplayedEvent(jsonPropositions));
        }
      });

      notRequestedMboxes.forEach(mbox => {
        const receivedMboxOfferCall: Promise<AlloyResponsePropositions | undefined> = new Promise((resolve, reject) => {
          notRequestedOffersCall.then(result => {
            const mboxResult = result.propositions.find(
              (offer) => offer.scope === mbox
            );

            resolve(mboxResult);
          }).catch(e => {
            reject(e);
          });

          this.controller.signal?.addEventListener('abort', reject);
        });

        this.cachedMboxes.set(`${mbox}_${JSON.stringify(parameters)}_${JSON.stringify(profileParameters)}`, receivedMboxOfferCall);
      });
    }

    const mboxesPromises = mboxNames.map(
      mboxName => this.cachedMboxes.get(`${mboxName}_${JSON.stringify(parameters)}_${JSON.stringify(profileParameters)}`)
    );
    const resolvedMboxes = await Promise.allSettled(mboxesPromises);

    const offersResult = mboxNames.reduce((acc, mboxName, index) => {
      acc[mboxName] = resolvedMboxes[index].status === 'fulfilled' ? parseIfJsonObjectOrArray(resolvedMboxes[index].value?.items?.[0]?.data?.content) : undefined;
      return acc;
    }, {} as Record<string, string | object | undefined>);

    return isInitialyArray ? offersResult : offersResult[mboxNames[0]];
  }

  public async getMboxProposition<T extends object = object>(
    param: { mboxName: string, parameters?: MboxParameters, profileParameters?: MboxProfileParameters }
  ): Promise<AlloyResponsePropositions<T> | undefined> {
    return await this.cachedMboxes.get(
      `${param.mboxName}_${JSON.stringify(param.parameters)}_${JSON.stringify(param.profileParameters)}`
    ) as AlloyResponsePropositions<T> | undefined;
  }

  public async trackOffer(param: { id: AlloyResponsePropositions["id"], scope: AlloyResponsePropositions["scope"], scopeDetails: AlloyResponsePropositions["scopeDetails"] }) {
    await window.alloyProxy('sendEvent', {
      type: 'decisioning.propositionInteract',
      xdm : {
        _experience: {
          decisioning: {
            propositions: [
              {
                id          : param.id,
                scope       : param.scope,
                scopeDetails: param.scopeDetails
              }
            ]
          }
        }
      }
    });
  }

  public async getContentFragment(param: {
    mboxName          : string,
    parameters?       : MboxParameters,
    profileParameters?: MboxProfileParameters
  }): Promise<DocumentFragment | null> {
    const mboxData = await this.getOffers({ ...param, mboxNames: param.mboxName });
    const fragment = document.createDocumentFragment();
    if (typeof mboxData === 'string') {
      const parser = new DOMParser();
      const doc = parser.parseFromString(mboxData, "text/html");

      fragment.append(...doc.body.childNodes);
      fragment.querySelectorAll("script").forEach(oldScript => {
        const newScript = document.createElement("script");
        [...oldScript.attributes].forEach(attr => newScript.setAttribute(attr.name, attr.value));
        newScript.textContent = oldScript.textContent;
        oldScript.replaceWith(newScript);
      });
      return fragment;
    }

    return null;
  }
}

window.BD = window.BD || {};
window.BD.state = window.BD.state || {};
